import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./UiDb";
import { resetAllState } from "./resetAllState";
import { BusinessProfile } from "../../interfaces/BusinessObject";
import { BusinessCollection } from "../../interfaces/BusinessCollectionObject";
import {
  fetchCollectionById,
  fetchMyCollections,
  createCollection,
  updateCollection,
  deleteCollection,
} from "../../services/thunks/BusinessThunk";
import { CollectionErrorCodes } from "../../interfaces/BusinessCollectionObject";
import CreateProductConfigHelper from "../../services/helpers/CreateProductConfigHelper";

/**
 * One collection's own state: its metadata (name / banner / curator / dates)
 * plus the businesses inside it — both filled by a single fetchCollectionById.
 *
 * Held here rather than in the business slice, whose lists mean "businesses I
 * own" and "the one business being viewed" — neither of which this is.
 *
 * Metadata is additionally seeded on navigation from search (setCollectionMeta)
 * so the banner and title paint before the fetch resolves.
 *
 * `myCollections` is a separate concern (the lead's own collections, for the
 * Admin "My Collections" screen) but lives in the same slice/file since it's
 * still business-collection domain state, same as OnboardingDb combining
 * summary/pendingBusinesses/team.
 *
 * loadingMap / errorMap / successMap follow the same shape as every other slice.
 */
interface BusinessCollectionState {
  collection: BusinessCollection | null;
  businesses: BusinessProfile[];
  /** 13001 / 404 — a wrong id. Distinct from a transient failure: no retry. */
  notFound: boolean;
  /** The lead's own collections (Admin → My Collections). */
  myCollections: BusinessCollection[];
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: BusinessCollectionState = {
  collection: null,
  businesses: [],
  notFound: false,
  myCollections: [],
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

const businessCollectionSlice = createSlice({
  name: "businessCollection",
  initialState,
  reducers: {
    /** Seeds metadata the caller already holds (search result → router state). */
    setCollectionMeta(state, action: PayloadAction<BusinessCollection>) {
      state.collection = action.payload;
    },
    clearCollection() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollectionById.pending, (state) => {
        state.loadingMap.fetchCollection = "Loading collection...";
        state.errorMap.fetchCollection = null;
        state.successMap.fetchCollection = false;
        state.notFound = false;
      })
      .addCase(fetchCollectionById.fulfilled, (state, action) => {
        state.loadingMap.fetchCollection = null;
        state.successMap.fetchCollection = true;
        // Server metadata is authoritative — it replaces whatever was seeded
        // from the search result on navigation.
        state.collection = action.payload.collection;
        state.businesses = action.payload.businesses;
      })
      .addCase(fetchCollectionById.rejected, (state, action) => {
        state.loadingMap.fetchCollection = null;
        state.businesses = [];
        // This thunk lets errors propagate (no rejectWithValue) so the message
        // and `code` survive on action.error via miniSerializeError.
        state.errorMap.fetchCollection =
          action.error?.message ?? "Couldn't load this collection.";
        state.notFound = action.error?.code === CollectionErrorCodes.NOT_FOUND;
      });

    // fetchMyCollections (Admin → My Collections list)
    builder
      .addCase(fetchMyCollections.pending, (state) => {
        state.loadingMap.fetchMyCollections = "Loading your collections...";
        state.errorMap.fetchMyCollections = null;
        state.successMap.fetchMyCollections = false;
      })
      .addCase(fetchMyCollections.fulfilled, (state, action) => {
        state.loadingMap.fetchMyCollections = null;
        state.successMap.fetchMyCollections = true;
        state.myCollections = action.payload;
      })
      .addCase(fetchMyCollections.rejected, (state, action) => {
        state.loadingMap.fetchMyCollections = null;
        state.errorMap.fetchMyCollections =
          action.error?.message ?? "Couldn't load your collections.";
      });

    // createCollection
    builder
      .addCase(createCollection.pending, (state) => {
        state.loadingMap.createCollection = "Creating collection...";
        state.errorMap.createCollection = null;
        state.successMap.createCollection = false;
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        state.loadingMap.createCollection = null;
        state.successMap.createCollection = true;
        state.myCollections.unshift(action.payload);
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.loadingMap.createCollection = null;
        state.errorMap.createCollection =
          action.error?.message ?? "Couldn't create this collection.";
      });

    // updateCollection
    builder
      .addCase(updateCollection.pending, (state) => {
        state.loadingMap.updateCollection = "Saving changes...";
        state.errorMap.updateCollection = null;
        state.successMap.updateCollection = false;
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        state.loadingMap.updateCollection = null;
        state.successMap.updateCollection = true;
        const updated = action.payload;
        const idx = state.myCollections.findIndex((c) => c.uid === updated.uid);
        if (idx >= 0) state.myCollections[idx] = updated;
        // Keep the currently-viewed collection page in sync without a refetch.
        if (state.collection?.uid === updated.uid) state.collection = updated;
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.loadingMap.updateCollection = null;
        state.errorMap.updateCollection =
          action.error?.message ?? "Couldn't update this collection.";
      });

    // deleteCollection — per-row loadingMap key (like OnboardingDb's
    // settleCaptain/regeneratePaymentLink) so deleting one row doesn't disable
    // every other row's delete button.
    builder
      .addCase(deleteCollection.pending, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = "Deleting...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        state.myCollections = state.myCollections.filter(
          (c) => c.uid !== action.payload
        );
        if (state.collection?.uid === action.payload) {
          state.collection = null;
          state.businesses = [];
        }
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = null;
        state.errorMap[key] =
          action.error?.message ?? "Couldn't delete this collection.";
      });

    builder.addCase(resetAllState, () => initialState);
  },
});

export const { setCollectionMeta, clearCollection } =
  businessCollectionSlice.actions;

export default businessCollectionSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

const selectSlice = (state: RootState) => state.businessCollection;
const selectSearchedCollections = (state: RootState) =>
  state.search.collections.results;

export const selectCollectionBusinesses = createSelector(
  [selectSlice],
  (slice) => slice.businesses
);

export const selectCollectionStatus = createSelector([selectSlice], (slice) => ({
  isLoading: Boolean(slice.loadingMap.fetchCollection),
  hasLoaded: Boolean(slice.successMap.fetchCollection),
  error: slice.errorMap.fetchCollection ?? null,
  notFound: slice.notFound,
}));

/**
 * Collection metadata for `uid`: the fetched/seeded collection, else the entry
 * still cached from the Collections search (which lets the title and banner
 * paint before the fetch resolves). Null only until the first response lands.
 */
export const selectCollectionMeta = createSelector(
  [selectSlice, selectSearchedCollections, (_: RootState, uid: string) => uid],
  (slice, searched, uid): BusinessCollection | null => {
    if (slice.collection?.uid === uid) return slice.collection;
    return searched.find((c) => c.uid === uid) ?? null;
  }
);

export interface CollectionTypeFilter {
  /** listingType id, or "" for the leading "All" chip. */
  id: string;
  label: string;
}

/**
 * Chips derived from the types actually present in this collection, in first-
 * seen order, behind a leading "All". Labels come from the business's own
 * listingTypeLabel when the backend set one, else the shared id→label map.
 */
export const selectCollectionTypeFilters = createSelector(
  [selectCollectionBusinesses],
  (businesses): CollectionTypeFilter[] => {
    const seen = new Map<string, string>();
    businesses.forEach((b) => {
      if (!b.listingType || seen.has(b.listingType)) return;
      seen.set(
        b.listingType,
        b.listingTypeLabel ||
          CreateProductConfigHelper.listingTypeIdToLabel(b.listingType)
      );
    });
    return [
      { id: "", label: "All" },
      ...Array.from(seen, ([id, label]) => ({ id, label })),
    ];
  }
);

// ─── My Collections (Admin) selectors ───────────────────────────────────────

export const selectMyCollections = createSelector(
  [selectSlice],
  (slice) => slice.myCollections
);

export const selectMyCollectionsStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isLoading: Boolean(slice.loadingMap.fetchMyCollections),
    hasLoaded: Boolean(slice.successMap.fetchMyCollections),
    error: slice.errorMap.fetchMyCollections ?? null,
  })
);

export const selectCreateCollectionStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isSubmitting: Boolean(slice.loadingMap.createCollection),
    error: slice.errorMap.createCollection ?? null,
  })
);

export const selectUpdateCollectionStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isSubmitting: Boolean(slice.loadingMap.updateCollection),
    error: slice.errorMap.updateCollection ?? null,
  })
);

/** Per-row delete status for a given collection id. */
export const selectDeleteCollectionStatus = createSelector(
  [selectSlice, (_: RootState, collectionId: string) => collectionId],
  (slice, collectionId) => ({
    isDeleting: Boolean(slice.loadingMap[`deleteCollection_${collectionId}`]),
    error: slice.errorMap[`deleteCollection_${collectionId}`] ?? null,
  })
);
